import React, { useEffect, useRef } from 'react';
interface TabsProps<T extends string> {
  tabs: readonly T[];
  value: T;
  onChange: (value: T) => void;
  label: string;
}

/**
 * A tab group that holds its content rather than shrinking it.
 *
 * - Labels keep the platform's readable 16px size at every width. The group
 *   never compresses type or padding to fit more tabs on screen.
 * - When the tabs are wider than the container, THIS container scrolls
 *   horizontally. The page never does: the scroll area is bounded by
 *   `max-w-full` and `min-w-0`, and each tab is `shrink-0` so labels
 *   cannot be squeezed into each other or clipped.
 * - Selecting a tab that sits off-screen scrolls it into view horizontally
 *   only, leaving vertical scroll position untouched.
 * - The active tab is marked by weight, colour AND a bottom indicator, so it
 *   never depends on colour alone. Weight is reserved on inactive tabs so the
 *   row does not shift when the selection changes.
 */
export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
  label
}: TabsProps<T>) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const scroller = scrollerRef.current;
    const active = activeRef.current;
    if (!scroller || !active) return;
    const tabLeft = active.offsetLeft;
    const tabRight = tabLeft + active.offsetWidth;
    const viewLeft = scroller.scrollLeft;
    const viewRight = viewLeft + scroller.clientWidth;
    const margin = 24;

    /* Scroll the strip, never the page — and only when the tab is out of view. */
    if (tabLeft < viewLeft + margin) {
      scroller.scrollTo({
        left: Math.max(0, tabLeft - margin),
        behavior: 'smooth'
      });
    } else if (tabRight > viewRight - margin) {
      scroller.scrollTo({
        left: tabRight - scroller.clientWidth + margin,
        behavior: 'smooth'
      });
    }
  }, [value]);
  return <div className="min-w-0 max-w-full border-b border-line" data-id="element-5395" data-mp="19bpdiextexbw">
      <div ref={scrollerRef} role="tablist" aria-label={label} className="cz-tabstrip flex max-w-full items-stretch gap-2 overflow-x-auto overflow-y-hidden" data-id="element-5396" data-mp="39d6w73y6road">
        {tabs.map(tab => {
        const active = tab === value;
        return <button key={tab} ref={active ? activeRef : undefined} role="tab" type="button" aria-selected={active} onClick={() => onChange(tab)} className={`-mb-px flex min-h-touch-lg shrink-0 items-center whitespace-nowrap border-b-[3px] px-4 py-3 text-body transition-colors duration-150 ease-out sm:px-5 ${active ? 'border-brand-700 font-semibold text-ink' : 'border-transparent font-medium text-ink-muted hover:border-line-strong hover:text-ink'}`} data-id="element-5397" data-mp="3j5clu70yttme">
              {tab}
            </button>;
      })}
      </div>
    </div>;
}